import { FC, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  CardMedia,
  CardActions,
  Button,
  IconButton,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import LinkIcon from '@mui/icons-material/Link'
import { Inventory as InventoryIcon } from '@mui/icons-material'
import {
  useGetInventoryItemsQuery,
  useUpdateInventoryItemMutation,
  useDeleteInventoryItemMutation,
} from '../../redux/hooks.ts'
import { ConfirmDialog } from '../../components/confirm-dialog.tsx'
import { InventorySearchSortControls } from '../../components/inventory-search-sort-controls.tsx'
import { useInventorySearchAndSort } from '../../hooks/use-inventory-search-and-sort.ts'
import type { InventoryItemModel } from '@itemize/server/models'
import {
  InventoryItemFormData,
  InventoryItemFormModal,
} from '../../components/inventory-item-form-modal.tsx'

export const Items: FC = () => {
  const { data: items, isLoading, error } = useGetInventoryItemsQuery()
  const [updateInventoryItem] = useUpdateInventoryItemMutation()
  const [deleteInventoryItem] = useDeleteInventoryItemMutation()

  const [editingItem, setEditingItem] = useState<InventoryItemModel | null>(null)
  const [deletingItem, setDeletingItem] = useState<InventoryItemModel | null>(null)

  const {
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    toggleSortOrder,
    filteredAndSortedItems,
  } = useInventorySearchAndSort({
    items,
    defaultSortBy: 'updatedAt',
    defaultSortOrder: 'desc',
  })

  const handleEditClick = (item: InventoryItemModel) => {
    setEditingItem(item)
  }

  const handleDeleteClick = (item: InventoryItemModel) => {
    setDeletingItem(item)
  }

  const handleEditSubmit = async (data: InventoryItemFormData, item: InventoryItemModel | null) => {
    await updateInventoryItem({ data, item: item }).unwrap()
  }

  const handleDeleteConfirm = async () => {
    if (deletingItem) {
      await deleteInventoryItem(deletingItem.id).unwrap()
      setDeletingItem(null)
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">Error loading items: {String(error)}</Alert>
  }

  if (!items || items.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="300px"
        textAlign="center"
      >
        <InventoryIcon sx={{ fontSize: 64, color: 'text.secondary', marginBottom: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No items found
        </Typography>
        <Typography color="text.secondary">Start by adding your first inventory item.</Typography>
      </Box>
    )
  }

  return (
    <Box>
      {/* Search and Sort Controls */}
      <Box sx={{ marginBottom: 3 }}>
        <InventorySearchSortControls
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortByChange={setSortBy}
          sortOrder={sortOrder}
          onToggleSortOrder={toggleSortOrder}
        />
      </Box>

      {filteredAndSortedItems.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="300px"
          textAlign="center"
        >
          <InventoryIcon sx={{ fontSize: 64, color: 'text.secondary', marginBottom: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No items found
          </Typography>
          <Typography color="text.secondary">
            {searchQuery.trim()
              ? 'Try adjusting your search terms'
              : 'Start by adding your first inventory item.'}
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {filteredAndSortedItems.length} item{filteredAndSortedItems.length !== 1 ? 's' : ''}{' '}
            found
            {searchQuery.trim() && items.length !== filteredAndSortedItems.length && (
              <> (filtered from {items.length} total)</>
            )}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 3,
              marginTop: 2,
            }}
          >
            {filteredAndSortedItems.map((item) => (
              <Card
                key={item.id}
                elevation={2}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardMedia
                  component="img"
                  // alt="green iguana"
                  height="200"
                  // width="200"
                  sx={{ padding: '1em 1em 0 1em', objectFit: 'contain', borderRadius: '1em' }}
                  image={item.imageUrl || '/images/image-placeholder.jpg'}
                />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* <Box display="flex" alignItems="center" gap={1} marginBottom={2}>
                    <InventoryIcon color="primary" />
                    <Chip
                      label={`ID: ${item.id.length > 26 ? item.id.substring(0, 26) + '...' : item.id}`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </Box> */}

                  <Typography variant="h4" component="h3" gutterBottom fontSize={16}>
                    {item.name}
                  </Typography>

                  {item.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        marginBottom: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {item.description}
                    </Typography>
                  )}

                  {item.webUrl && (
                    <Box sx={{ marginBottom: 2 }}>
                      <Button
                        size="small"
                        startIcon={<LinkIcon />}
                        onClick={() => window.open(item.webUrl!, '_blank', 'noopener,noreferrer')}
                        sx={{ textTransform: 'none' }}
                      >
                        {new URL(item.webUrl).host.replace(/^www\./, '')}
                      </Button>
                    </Box>
                  )}

                  <Box display="flex" flexDirection="column" gap={1} sx={{ marginTop: 'auto' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      <strong>Price:</strong> ${(item.priceCents / 100).toFixed(2)}
                    </Typography>
                    {/* 
                    <Typography variant="caption" color="text.secondary">
                      Created: {new Date(item.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Updated: {new Date(item.updatedAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {item.id}
                    </Typography> */}
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <IconButton aria-label="Edit" onClick={() => handleEditClick(item)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton aria-label="Delete" onClick={() => handleDeleteClick(item)}>
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            ))}
          </Box>
        </>
      )}

      {/* Edit Modal */}
      <InventoryItemFormModal
        open={!!editingItem}
        onClose={() => setEditingItem(null)}
        onSubmit={handleEditSubmit}
        item={editingItem}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deletingItem}
        onClose={() => setDeletingItem(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Item"
        message={
          <>
            Are you sure you want to delete <b>"{deletingItem?.name}"</b>?
          </>
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  )
}
